// Shared Q&A Item Component
// Displays Q&A items in both main page and admin contexts
// Follows the Section, Component & Manager System architecture

import React from 'react';
import { linkUtils } from '../../../core/utils';
import { LearnQaAnswerMarkdown } from './LearnQaAnswerMarkdown';

export function QAItem({
  qa,
  isAdmin = false,
  onLikeToggle,
  onEdit,
  onDelete,
  user // for like functionality
}) {
  const { usedLinkNames } = linkUtils.processInlineLinks(qa.answer, qa.links || []);

  // Filter out links that are used inline from the related links section
  const relatedLinks = (qa.links || []).filter(link => !usedLinkNames.has(link.title));
  if (isAdmin) {
    // Admin version - condensed display with admin controls
    return (
      <div className="learn-featured-qa admin-qa-item">
        <div className="learn-qa-content">
          <h3 className="learn-qa-question">{qa.question}</h3>
          <div className="learn-qa-answer-preview learn-qa-answer-markdown-host learn-qa-answer-markdown-host--preview">
            <LearnQaAnswerMarkdown qa={qa} />
          </div>
        </div>
        <div className="learn-qa-stats">
          {qa.tags && qa.tags.length > 0 && (
            <span className="learn-qa-category">
              {qa.tags.join(', ')}
            </span>
          )}
          {(!qa.tags || qa.tags.length === 0) && (
            <span className="learn-qa-spacer"></span>
          )}
          <span className="learn-qa-helpful">
            <i className="fa-solid fa-thumbs-up"></i> {qa.likes || 0}
          </span>
        </div>

        {/* Links section for admin view */}
        {relatedLinks && relatedLinks.length > 0 && (
          <div className="learn-qa-links admin-links">
            <div className="qa-links-list">
              {relatedLinks.map((link, index) => (
                <a
                  key={index}
                  href={linkUtils.processQALink(link.url, link.type)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qa-link-item"
                >
                  <span className="qa-link-title">{link.title}</span>
                  <i className="fa-solid fa-external-link-alt"></i>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Admin Actions */}
        <div className="admin-qa-actions">
          <button
            className="admin-action-btn admin-edit-btn"
            onClick={() => onEdit && onEdit(qa)}
            title="Edit Q&A"
          >
            <i className="fa-solid fa-edit"></i>
          </button>
          <button
            className="admin-action-btn admin-delete-btn"
            onClick={() => onDelete && onDelete(qa)}
            title="Delete Q&A"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    );
  }

  // Main page version - full display with like functionality
  return (
    <div className="learn-qa-item" id={`learn-qa-card-${qa.id}`}>
      <div className="learn-qa-header">
        <div className="learn-qa-header-left">
          <h3 className="learn-qa-question">{qa.question}</h3>
        </div>
        <div className="learn-qa-header-right">
          <button
            className={`learn-qa-helpful ${qa.userLiked ? 'liked' : ''}`}
            onClick={() => onLikeToggle && onLikeToggle(qa.id)}
            disabled={!user}
            title={user ? (qa.userLiked ? 'Unlike this answer' : 'Like this answer') : 'Sign in to like answers'}
          >
            <i className="fa-solid fa-thumbs-up"></i> {qa.helpful} found this helpful
          </button>
        </div>
      </div>
      <div className="learn-qa-answer learn-qa-answer-markdown-host">
        <LearnQaAnswerMarkdown qa={qa} />
      </div>
      {qa.tags && qa.tags.length > 0 && (
        <div className="learn-qa-tags">
          {qa.tags.join(', ')}
        </div>
      )}

      {/* Links section */}
      {relatedLinks && relatedLinks.length > 0 && (
        <div className="learn-qa-links">
          <h4>Related Links</h4>
          <div className="qa-links-list">
            {relatedLinks.map((link, index) => (
              <a
                key={index}
                href={linkUtils.processQALink(link.url, link.type)}
                target="_blank"
                rel="noopener noreferrer"
                className="qa-link-item"
              >
                <span className="qa-link-title">{link.title}</span>
                <i className="fa-solid fa-external-link-alt"></i>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
